import React, { useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { LockOutlined } from "@mui/icons-material";
import { CssTextField } from "./TextField";

/**
 * Locked, confirmation-guarded editor for the critical `trade_name` field
 * (the GST / company name printed on bills). Render only when billing + GST
 * are enabled. The field cannot be typed into directly — editing goes through
 * an explicit confirm dialog with an acknowledgement checkbox, and the value
 * still only persists on the parent form's Submit.
 *
 * @param {string}   value    current trade name
 * @param {function} onChange called with the trimmed new value on Apply
 * @param {object}   colors   theme tokens (for dialog surface color)
 */
const TradeNameField = ({ value, onChange, colors }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [ack, setAck] = useState(false);

  const openDialog = () => {
    setDraft(value || "");
    setAck(false);
    setOpen(true);
  };

  const apply = () => {
    onChange((draft || "").trim());
    setOpen(false);
  };

  return (
    <Stack direction="row" alignItems="flex-start" spacing={2}>
      <Typography mt={0.75} width={"50%"}>
        Trade Name (on bills):
      </Typography>
      <Stack flexGrow={1} spacing={0.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CssTextField
            fullWidth
            size="small"
            type="text"
            placeholder="Not set — bills use the hotel name"
            value={value || ""}
            disabled
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<LockOutlined fontSize="small" />}
            onClick={openDialog}
          >
            Edit
          </Button>
        </Stack>
        <Typography variant="caption" color="#999">
          Critical: appears as the GST / company name on every bill (including
          past bills). Editing is locked — use the Edit button.
        </Typography>
      </Stack>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { bgcolor: colors?.primary?.[400], backgroundImage: "none" },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Edit Trade Name</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="#bbb" sx={{ mb: 2 }}>
            Printed as the GST / company name on all bills, including past ones.
          </Typography>
          <CssTextField
            autoFocus
            fullWidth
            size="small"
            label="Trade Name"
            placeholder="Blank = use hotel name"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                size="small"
                color="secondary"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption">
                I understand this appears on all GST bills.
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={!ack}
            onClick={apply}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default TradeNameField;
